/**
 * Turning shot-stat figures into the one thing the page is for: what to practise.
 *
 * Every function here is pure and every one of them can answer "nothing" —
 * which is the point. A sentence about a golfer's game is a claim, and a claim
 * needs a sample. The server publishes `sample.enough` per department and each
 * generator below refuses to speak without it, so a single logged hole can
 * never name the weakest part of somebody's bag.
 *
 * The figures themselves are never computed here. `services/shot_stats.py` is
 * the one place that decides what they mean; this file only decides how to say
 * them.
 */

export const DEPARTMENTS = [
  // The order a hole is played — tee, approach, around the green, on it —
  // because that is how a player narrates their own round, so each card lands
  // where they already expect the next answer.
  { key: 'driving', label: 'Driving' },
  { key: 'approach', label: 'Approach' },
  { key: 'short_game', label: 'Short game' },
  { key: 'putting', label: 'Putting' },
];

export const BAND_ORDER = ['lt100', '100_125', '125_150', '150_175', 'gt175', 'mid', 'long'];

/** A percentage for display. Null is "not logged yet", never 0%. */
export const showPct = (v) => (v == null ? '—' : `${Math.round(v)}%`);

export const showNum = (v, dp = 1) => (v == null ? '—' : v.toFixed(dp));

const RATIO_WORDS = [
  [4.5, null],            // beyond this, no multiplier reads honestly
  [3.5, 'about four times'],
  [2.5, 'about three times'],
  [1.75, 'about twice'],
];

const describeRatio = (r) => {
  for (const [floor, words] of RATIO_WORDS) {
    if (r >= floor) return words;
  }
  return null;
};

/** Where the tee ball goes when it misses — the coachable half of a fairway %. */
export const drivingSentence = (driving, enough) => {
  if (!enough || !driving) return null;
  const { miss_left_pct: left, miss_right_pct: right } = driving;
  if (left == null || right == null) return null;
  const [big, small, side, other] = right >= left
    ? [right, left, 'right', 'left']
    : [left, right, 'left', 'right'];
  if (big < 12) return null;
  if (!small) return `Almost every miss goes ${side}.`;
  const ratio = big / small;
  if (ratio < 1.75) return null;
  const words = describeRatio(ratio);
  const figures = `${Math.round(big)}% against ${Math.round(small)}%`;
  return words
    ? `Your tee shots miss ${side} ${words} as often as ${other} — ${figures}.`
    : `Your misses go ${side} far more often than ${other} — ${figures}.`;
};

const DIRECTION_SENTENCE = {
  short: (n, of) => `Most of your missed greens finish short — ${n} of ${of}. That is usually a club, not a swing.`,
  long: (n, of) => `You go through the green more than you miss it any other way — ${n} of ${of}.`,
  left: (n, of) => `Your approach misses lean left — ${n} of ${of}.`,
  right: (n, of) => `Your approach misses lean right — ${n} of ${of}.`,
};

/**
 * Preferring the longest distance band that misses short consistently, because
 * "from 150 yards you are short" names both the club and the fix, where an
 * aggregate only names a tendency.
 */
export const approachSentence = (approach, labels, enough) => {
  if (!enough || !approach) return null;
  const byBand = approach.miss_direction_by_band || {};

  const bands = BAND_ORDER.filter(b => byBand[b]);
  for (const band of [...bands].reverse()) {
    const d = byBand[band];
    if ((d.holes || 0) >= 4 && d.short / d.holes >= 0.6) {
      return `From ${labels[band]} yards you miss short ${d.short} times in ${d.holes}.`;
    }
  }

  const totals = { short: 0, long: 0, left: 0, right: 0 };
  let holes = 0;
  bands.forEach((b) => {
    Object.keys(totals).forEach((k) => { totals[k] += byBand[b][k] || 0; });
    holes += byBand[b].holes || 0;
  });
  if (holes < 6) return null;
  const [dir, n] = Object.entries(totals).sort((a, b) => b[1] - a[1])[0];
  if (n / holes < 0.45) return null;
  return DIRECTION_SENTENCE[dir](n, holes);
};

/** Sand against everywhere else — the reason those two are published apart. */
export const shortGameSentence = (shortGame, enough) => {
  if (!enough || !shortGame) return null;
  const {
    net_scramble_bunker_pct: bunker, net_scramble_bunker_holes: bunkerHoles,
    net_scramble_non_bunker_pct: other, net_scramble_non_bunker_holes: otherHoles,
  } = shortGame;
  if (bunker == null || other == null || bunkerHoles < 5 || otherHoles < 5) return null;
  const figures = `${Math.round(bunker)}% from sand against ${Math.round(other)}% from everywhere else`;
  if (other - bunker >= 15) return `Sand is the leak — ${figures}.`;
  if (bunker - other >= 15) return `Bunkers are not your problem — ${figures}.`;
  return null;
};

export const puttingSentence = (putting, enough) => {
  if (!enough || !putting) return null;
  const { three_putts_per_18: threes, putts_per_gir_hole: onGreens } = putting;
  if (threes != null && threes >= 2.5) {
    return `Three-putts are the cost — about ${threes.toFixed(1)} in every 18 holes.`;
  }
  if (onGreens != null && onGreens >= 2.1) {
    return `On the greens you do hit, you are taking ${onGreens.toFixed(2)} putts.`;
  }
  if (threes != null && threes <= 1.0) {
    return `You rarely three-putt — about ${threes.toFixed(1)} in every 18 holes.`;
  }
  return null;
};

/* ──────────────────────────────────────────────────────────────────────────
   The broadcast read: what the heat maps are allowed to say.

   Everything below serves the visuals, and every one of these functions can
   answer "not yet". A heat map is a confident thing — a shape, a colour, a
   position — and confidence from three tee shots is a lie told well. So the
   drawing is gated on a SAMPLE TIER (below), the claim on the server's floor,
   and the two are deliberately different: a blob can appear before a sentence
   is allowed to name a fault.
   ────────────────────────────────────────────────────────────────────────── */

/**
 * How much of the visual a sample has earned.
 *
 *   thin  (< 8 shots)  outlines and individual shots only — no blobs, no
 *                      percentage headline, counts instead of rates
 *   low   (8–24)       blobs at reduced opacity, and a LOW SAMPLE chip
 *   full  (25+)        the whole treatment
 *
 * These are the VISUAL tiers. Whether a figure may make a CLAIM is a separate,
 * higher bar the server sets per department (`sample.enough`).
 */
export const TIERS = { low: 8, full: 25 };

export const tierFor = (count) => {
  if (!(count > 0)) return 'empty';
  if (count >= TIERS.full) return 'full';
  if (count >= TIERS.low) return 'low';
  return 'thin';
};

/** The four departments as the page needs them — labels, colours, wording. */
export const AREAS = [
  {
    key: 'driving', slug: 'driving', label: 'Driving', tone: 'drive',
    unit: 'tee shots', affordance: 'Corridor map', suffix: 'fairways',
  },
  {
    key: 'approach', slug: 'approach', label: 'Approach', tone: 'appr',
    unit: 'holes', affordance: 'Green heat map', suffix: 'greens',
  },
  {
    key: 'short_game', slug: 'short-game', label: 'Short game', tone: 'short',
    unit: 'chances', affordance: 'Save zones', suffix: 'up and down',
  },
  {
    key: 'putting', slug: 'putting', label: 'Putting', tone: 'putt',
    unit: 'holes', affordance: 'Putt rings', suffix: 'per 18',
  },
];

export const areaBySlug = (slug) => AREAS.find(a => a.slug === slug) || null;

/**
 * The next number worth logging towards, and never an arbitrary target.
 *
 * The ladder is the two visual tiers and then the department's own claim
 * floor, so the chip on a card always counts down to something that actually
 * changes on screen when it is reached.
 */
export const nextThreshold = (count, floor) => {
  const rungs = [TIERS.low, TIERS.full, floor].filter(n => n != null);
  const next = rungs.sort((a, b) => a - b).find(n => count < n);
  return next == null ? null : next - count;
};

/**
 * The cards in the order the hero puts them: worst first.
 *
 * A department with no verdict — no data, or below its floor — cannot be
 * called better or worse than one with a number, so it keeps its place in the
 * order a hole is played and sits after everything that can be ranked.
 */
export const orderAreas = (benchmark) => {
  const behind = benchmark?.bands_behind || {};
  const ranked = AREAS.filter(a => behind[a.key] != null)
    .sort((a, b) => behind[b.key] - behind[a.key]);
  return [...ranked, ...AREAS.filter(a => behind[a.key] == null)];
};

/**
 * The hero bar: one segment per department, width by SHARE of the shortfall.
 *
 * Only shortfalls are drawn, for the same reason only shortfalls are added up
 * (see `_bands_behind_total` on the server) — a bar segment for a strength
 * would read as another thing to fix.
 */
export const heroSegments = (benchmark) => {
  const behind = benchmark?.bands_behind || {};
  const total = AREAS.reduce((sum, a) => sum + Math.max(0, behind[a.key] || 0), 0);
  if (!total) return [];
  return AREAS
    .filter(a => (behind[a.key] || 0) > 0)
    .map(a => ({ ...a, bands: behind[a.key], share: (behind[a.key] / total) * 100 }))
    .sort((a, b) => b.bands - a.bands);
};

/**
 * How the trend reads, or null when there is nothing honest to say.
 *
 * THE DIRECTION, NEVER THE FIGURE. `trend.delta` is a difference between two
 * `bands_behind_total`s, and that total is the figure this screen stopped
 * showing precisely because it cannot be read: a band is a unit invented so
 * four departments in four units can be SORTED, and summed it is a quantity of
 * nothing. Printed bare — "7.7 worse than your previous 4 rounds" — it read as
 * shots, directly under a headline written to avoid exactly that.
 *
 * The SIGN of that difference is still worth having: it is the same comparison
 * on both halves, so which way it moved is a real answer even though its size
 * is not. So the arrow keeps the direction, the dead band and the number of
 * rounds, and says which departments it had enough of to compare.
 */
export const trendLabel = (trend, band) => {
  if (!trend || trend.delta == null) return null;
  // Nothing behind in either half: there is no shortfall to have moved, and
  // "no different" would describe a gap that does not exist.
  if (trend.total === 0 && trend.previous === 0) return null;
  // BETTER OR WORSE, never "closer to" or "further from". A player ahead in
  // two departments and behind in two is both close to and far from a typical
  // player at once, so a distance word cannot say which way they moved — the
  // first rewrite of this line said "further from", and that read as either.
  const frame = `Against a typical ${band ? `${band} handicapper` : 'player off your handicap'}`;
  const n = trend.rounds;
  if (Math.abs(trend.delta) < 0.2) {
    return { direction: 'level', text: `${frame}, your last ${n} rounds were no different from the ${n} before` };
  }
  const better = trend.delta < 0;
  return {
    direction: better ? 'better' : 'worse',
    text: `${frame}, your last ${n} rounds were ${better ? 'better' : 'worse'} than the ${n} before`,
  };
};

/**
 * The headline figure on a card.
 *
 * Below the thin tier it is a COUNT, never a rate: "3 of 3 fairways" is the
 * whole truth about three tee shots, where 100% is a claim about a golfer.
 */
export const cardFigure = (areaKey, data) => {
  const d = data || {};
  switch (areaKey) {
    case 'driving': {
      const n = d.driving?.tee_shots || 0;
      if (tierFor(n) === 'thin') {
        const hit = Math.round(((d.driving?.fairway_pct || 0) / 100) * n);
        return { value: n ? `${hit} of ${n}` : '—', suffix: 'fairways', small: true };
      }
      return { value: Math.round(d.driving.fairway_pct), unit: '%', suffix: 'fairways' };
    }
    case 'approach': {
      const n = d.approach?.gir_holes || 0;
      if (tierFor(n) === 'thin') {
        const hit = Math.round(((d.approach?.gir_pct || 0) / 100) * n);
        return { value: n ? `${hit} of ${n}` : '—', suffix: 'greens', small: true };
      }
      return { value: Math.round(d.approach.gir_pct), unit: '%', suffix: 'greens in regulation' };
    }
    case 'short_game': {
      const n = d.short_game?.net_scramble_holes || 0;
      if (tierFor(n) === 'thin') {
        const saved = Math.round(((d.short_game?.net_scramble_pct || 0) / 100) * n);
        return { value: n ? `${saved} of ${n}` : '—', suffix: 'up and down', small: true };
      }
      return { value: Math.round(d.short_game.net_scramble_pct), unit: '%', suffix: 'up and down' };
    }
    case 'putting': {
      const n = d.putting?.holes_with_putts || 0;
      // Putts per 18 is an average, not a rate out of attempts, so it survives
      // a small sample better — but it is still extrapolated, and from four
      // holes that extrapolation is the whole figure.
      if (tierFor(n) === 'thin') {
        return { value: n ? showNum(d.putting.putts_per_18, 1) : '—',
          suffix: `putts per 18, from ${n} holes`, small: true };
      }
      return { value: showNum(d.putting.putts_per_18, 1), suffix: 'putts per 18' };
    }
    default:
      return { value: '—' };
  }
};

/**
 * The same four headline figures, small enough for a summary strip.
 *
 * The rule that matters is the one the cards downstairs follow: below the thin
 * tier a rate becomes the COUNT it was taken from. "100%" over the word FWY,
 * from three tee shots, is the single most misleading thing this feature has
 * ever put on a screen — it is what the summary card on My Stats showed.
 *
 * Putts per 18 stays a number at every sample size because it is an average
 * rather than a rate out of attempts, but it is EXTRAPOLATED — three logged
 * holes times six — so the strip flags itself as thin and the count travels
 * with it.
 */
export const compactFigures = (data) => {
  const share = (rate, n) => `${Math.round(((rate || 0) / 100) * n)}/${n}`;
  const drive = data?.driving?.tee_shots || 0;
  const appr = data?.approach?.gir_holes || 0;
  const short = data?.short_game?.net_scramble_holes || 0;
  const putts = data?.putting?.holes_with_putts || 0;
  const figure = (count, rate, label, tone, value) => ({
    label, tone, count,
    thin: tierFor(count) === 'thin',
    value: count === 0 ? '—'
      : tierFor(count) === 'thin' ? (value ?? share(rate, count))
        : (value ?? showPct(rate)),
  });
  return [
    figure(drive, data?.driving?.fairway_pct, 'FWY', 'drive'),
    figure(appr, data?.approach?.gir_pct, 'GIR', 'appr'),
    figure(short, data?.short_game?.net_scramble_pct, 'U&D', 'short'),
    figure(putts, null, 'PUTTS', 'putt', putts ? showNum(data?.putting?.putts_per_18, 1) : '—'),
  ];
};

const pct = (v) => `${Math.round(v)}%`;

/**
 * The five lies in a sentence — "from deep rough you save 21%".
 *
 * The server folds the two legacy answers onto their nearest modern one before
 * anything gets here (`LEGACY_LIES` in shot_stats.py), so nothing downstream
 * has to know they existed. They stay in the map below anyway: a payload from
 * a phone running a frozen bundle can still carry one.
 */
export const LIE_WORDS = {
  // 'fringe' is fairway-or-fringe — see MISS_LIE_LABELS in holeStats.js. The
  // sentence takes the shorter half of the pair ("from short grass you save
  // 62%"), because the distinction the figure turns on is grass you can putt
  // from against grass you cannot.
  fringe: 'short grass', light: 'the light rough', deep: 'deep rough',
  bunker: 'sand', hazard: 'a hazard',
  rough: 'the rough', other: 'elsewhere',
};

/** The same four lies as a label rather than as part of a sentence. */
export const LIE_LABELS = {
  fringe: 'Fairway or fringe', light: 'Light rough', deep: 'Deep rough',
  bunker: 'Sand', hazard: 'Water',
  rough: 'Rough', other: 'Elsewhere',
};

/** The two the ball can still be putted or bumped from. */
export const RECOVERABLE_LIES = ['fringe', 'light'];

/**
 * One line of pattern under the headline — the thing the number cannot say.
 *
 * `%s` marks the figure the card sets in the brighter colour, so the emphasis
 * lives with the sentence that earned it rather than being guessed at by a
 * regular expression in the view.
 */
export const patternFor = (areaKey, data) => {
  const d = data || {};
  switch (areaKey) {
    case 'driving': {
      const dr = d.driving || {};
      if (tierFor(dr.tee_shots) === 'thin') return null;
      const { miss_left_pct: left, miss_right_pct: right, penalty_tee_shot_pct: gone } = dr;
      if (left == null || right == null) return null;
      const lean = right > left ? 'right' : 'left';
      const spread = Math.abs((right || 0) - (left || 0));
      if (gone >= 15) return { text: `%s of tee shots find trouble`, figure: pct(gone) };
      if (spread < 8) return { text: 'misses split evenly, %s finding trouble', figure: pct(gone || 0) };
      return { text: `misses lean ${lean} — %s against ${pct(lean === 'right' ? left : right)}`,
        figure: pct(lean === 'right' ? right : left) };
    }
    case 'approach': {
      const mix = d.approach?.miss_mix || {};
      // The tier gate belongs on every branch, not just driving's: "100% of
      // your missed greens finish short" from two missed greens is the exact
      // sentence this page exists not to write.
      if (!mix.holes || tierFor(d.approach?.gir_holes) === 'thin') return null;
      const [dir, n] = Object.entries(mix)
        .filter(([k]) => k !== 'holes')
        .sort((a, b) => b[1] - a[1])[0] || [];
      if (!n) return null;
      return { text: `%s of your missed greens finish ${dir}`, figure: pct((n / mix.holes) * 100) };
    }
    case 'short_game': {
      if (tierFor(d.short_game?.net_scramble_holes) === 'thin') return null;
      const zones = (d.short_game?.net_scramble_by_lie || []).filter(z => z.holes >= 3);
      if (!zones.length) return null;
      const worst = [...zones].sort((a, b) => a.net_scramble_pct - b.net_scramble_pct)[0];
      return { text: `from ${LIE_WORDS[worst.lie] || worst.lie} you save %s`,
        figure: pct(worst.net_scramble_pct) };
    }
    case 'putting': {
      const p = d.putting || {};
      if (tierFor(p.holes_with_putts) === 'thin') return null;
      if (p.three_putts_per_18 != null && p.three_putts_per_18 >= 1.5) {
        return { text: '%s three-putts every 18 holes', figure: p.three_putts_per_18.toFixed(1) };
      }
      if (p.one_putt_pct != null) return { text: '%s of holes need one putt', figure: pct(p.one_putt_pct) };
      return null;
    }
    default:
      return null;
  }
};

/**
 * Which part of PUTTING ITSELF is costing shots, or null when neither is.
 *
 * The two causes a putting stroke owns: three-putting, and taking too many on
 * the greens that were hit in regulation (the first putt finishing too far
 * away). Where neither fires and the putting TOTAL is still high, the extra
 * putts are coming after missed greens — a chip that did not finish close
 * enough to one-putt — and that is the short game's problem showing up in the
 * putting column. Raw putts a round cannot tell the two apart, which is why
 * it is never ranked as the thing to fix on its own say-so.
 *
 * The thresholds are the ones the Putting tab has always used.
 */
export const PUTTING_CAUSE = { threePutts: 2, puttsPerGir: 2.1 };

export const puttingCause = (data) => {
  const p = data?.putting || {};
  if ((p.three_putts_per_18 || 0) >= PUTTING_CAUSE.threePutts) return 'three_putts';
  if ((p.putts_per_gir_hole || 0) >= PUTTING_CAUSE.puttsPerGir) return 'first_putt';
  return null;
};

/**
 * Is a high putting TOTAL a symptom rather than a cause?
 *
 * Yes when the total is behind the band, neither cause a putting stroke owns
 * fires, and there are greens-hit-in-regulation figures to rule the second
 * one out — without those, "not the stroke" would be a guess.
 */
export const puttingIsSymptom = (data) => !!puttingGap(data)?.behind
  && !puttingCause(data)
  && data?.putting?.putts_per_gir_hole != null;

/** The figures that clear the putting stroke, said once for both callers. */
export const onGreenFacts = (putting) => {
  const p = putting || {};
  const threes = p.three_putts_per_18 == null
    ? '' : `, and you three-putt ${showNum(p.three_putts_per_18, 1)} times a round`;
  return `on the greens you hit in regulation you take ${showNum(p.putts_per_gir_hole, 2)} putts${threes}`;
};

/**
 * The one sentence at the foot of a drill-down. It names a DECISION.
 *
 * "Aim a club longer" is a thing to do on Saturday; "your GIR is below
 * average" is a thing to feel. Null until the department has earned a claim,
 * because a decision from four holes is worse than no advice at all.
 */
export const decisionFor = (areaKey, data, enough) => {
  if (!enough) return null;
  const d = data || {};
  switch (areaKey) {
    case 'driving': {
      const dr = d.driving;
      const lean = (dr.miss_right_pct || 0) > (dr.miss_left_pct || 0) ? 'right' : 'left';
      const big = Math.max(dr.miss_right_pct || 0, dr.miss_left_pct || 0);
      if ((dr.penalty_tee_shot_pct || 0) >= 12) {
        return `Trouble off the tee costs you ${showNum(dr.penalties_per_round, 1)} shots a round — take less club on the holes where the miss is punished.`;
      }
      if (big >= 30) {
        return `Your miss is ${lean}. Aim down the ${lean === 'right' ? 'left' : 'right'} half of the fairway and let the shot you actually hit finish in the middle.`;
      }
      return `You are missing both ways — start with the strike before the aim.`;
    }
    case 'approach': {
      const mix = d.approach?.miss_mix || {};
      if (!mix.holes) return null;
      const short = ((mix.short || 0) / mix.holes) * 100;
      const long = ((mix.long || 0) / mix.holes) * 100;
      if (short >= 40) return `Most of your misses are short — take one more club and aim for the middle of the green, not the flag.`;
      if (long >= 40) return `You go through greens more than you come up short — the number on the card is not the number you are hitting it.`;
      return `Your misses are sideways rather than short — the club is right, the face is not.`;
    }
    case 'short_game': {
      const zones = (d.short_game?.net_scramble_by_lie || []).filter(z => z.holes >= 5);
      if (!zones.length) return null;
      const worst = [...zones].sort((a, b) => a.net_scramble_pct - b.net_scramble_pct)[0];
      return `${pct(worst.net_scramble_pct)} saved from ${LIE_WORDS[worst.lie] || worst.lie} over ${worst.holes} chances — that is the shot to take to the practice ground.`;
    }
    case 'putting': {
      const p = d.putting || {};
      const cause = puttingCause(d);
      if (cause === 'three_putts') {
        return `Three-putts, not missed short ones, are the cost — practise pace from 30 feet rather than the four-footers.`;
      }
      if (cause === 'first_putt') {
        return `On the greens you hit you are taking ${showNum(p.putts_per_gir_hole, 2)} putts — the first putt is finishing too far away.`;
      }
      // THE PUTTER IS ONLY LET OFF WHERE THE FIGURES LET IT OFF.
      //
      // "Leave it alone" used to be unconditional, and the verdict asks this
      // about the department it has just named WORST — so a player reading
      // "34.4 putts against a typical 32.2" was told two lines later that
      // putting was not where their shots were going.
      //
      // Where the total is behind and `puttingCause` finds neither cause a
      // putting stroke owns, the extra putts are coming after missed greens.
      // The figures cannot say whether the chip finished too far away or the
      // putt after it did not drop, and they do not need to: "get up and down
      // more often" is true of both, and it names the department.
      //
      // NAME THE SHOT. The first version ended "the shot to practise is the one
      // that gets you on, not the one that finishes" — meaning the chip, and
      // read by the person it was written for as the APPROACH, which the same
      // screen called a strength.
      const gap = puttingGap(d);
      if (gap?.behind) {
        if (!puttingIsSymptom(d)) {
          return `You take more putts than a typical player off your handicap, but three-putts are not the reason — and too few greens hit in regulation are logged to say what is.`;
        }
        // "Most", not all: hitting more greens than typical also adds putts
        // (a green hit in regulation is usually two), so the missed greens
        // are the larger share of the surplus rather than the whole of it.
        return departmentGap(d, 'short_game')?.behind
          ? `Your putting stroke is not the problem: ${onGreenFacts(p)}. Most of the extra putts come after missed greens, so getting up and down more often is what brings the total down.`
          : `Your putting stroke is not the problem: ${onGreenFacts(p)}. Leave it alone.`;
      }
      // Genuinely not the problem. Say where the problem IS rather than
      // guessing at "the long game", which is not always it.
      const elsewhere = weakestStanding(d);
      return elsewhere
        ? `Putting is not where your shots are going — leave it alone. Your ${elsewhere.label.toLowerCase()} is further behind.`
        : `Putting is not where your shots are going — leave it alone.`;
    }
    default:
      return null;
  }
};

/**
 * Blob radius from a share of shots, area-proportional.
 *
 * Radius scaled by the square root, so a zone holding twice the shots looks
 * twice the size rather than four times it — the classic way a bubble lies.
 */
export const blobRadius = (share, min, max) => {
  const s = Math.max(0, Math.min(100, share || 0)) / 100;
  return min + (max - min) * Math.sqrt(s);
};

/* --- Where your shots go ---------------------------------------------------
 *
 * The strokes half of this page, and a different KIND of figure from
 * everything above it. Every other number here is a rate compared with a
 * benchmark table, so it can only be expressed in handicap bands — the ruler
 * the server invented precisely because a fairway percentage cannot be turned
 * into strokes without a coefficient nobody has sourced.
 *
 * These are strokes that were actually played. `shots_lost` on the payload is
 * an accounting, not an estimate: on every hole the three buckets add back to
 * the score over par, so there is nothing to compare against and nothing to
 * invent. That is why this block makes claims the hero deliberately will not.
 */

export const SHOT_BUCKETS = [
  // Reaching the green first, then the two things that happen to a hole after
  // it should have been over — the order a shot is lost in.
  //
  // TEE TO GREEN, NOT "LONG GAME". The bucket is the remainder once putts and
  // penalties are taken out, so it holds every chip, pitch and bunker shot as
  // well as the tee shots and approaches. Labelled "Long game" and painted in
  // Driving's blue, a player whose weakness is the short game read "+10 long
  // game" under a verdict calling their long game a strength, and concluded
  // the page was contradicting itself — it was mislabelling. "Tee to green"
  // is the golf term for exactly this span, and it wears no department's
  // colour because it belongs to three of them. The server key is unchanged:
  // shipped phone builds read `long_game`.
  { key: 'long_game', label: 'Tee to green', tone: 't2g' },
  { key: 'penalties', label: 'Penalties', tone: 'pen' },
  { key: 'putting', label: 'Putting', tone: 'putt' },
];

/** Strokes, signed, because a bucket can be a gain. Null is never 0.0. */
export const showShots = (v, dp = 1) => {
  if (v == null) return '—';
  const n = Number(v.toFixed(dp));
  return `${n > 0 ? '+' : ''}${n.toFixed(dp)}`;
};

/**
 * The bucket bar: one segment per bucket, width by share of the shots LOST.
 *
 * Only the positive buckets are drawn, the same rule the hero bar follows: a
 * segment for a bucket a player is winning shots in would read as another
 * thing to fix. The buckets that are negative still show their figure in the
 * row beneath — they are the good news, and it is worth reading.
 */
export const shotBucketSegments = (shotsLost) => {
  const per18 = shotsLost?.per_18 || {};
  const lost = SHOT_BUCKETS.map(b => ({ ...b, shots: per18[b.key] }))
    .filter(b => b.shots != null && b.shots > 0);
  const total = lost.reduce((sum, b) => sum + b.shots, 0);
  if (!total) return [];
  return lost
    .map(b => ({ ...b, share: (b.shots / total) * 100 }))
    .sort((a, b) => b.shots - a.shots);
};

/** Every bucket with its figure, in play order, for the row under the bar. */
export const shotBucketRows = (shotsLost) => {
  const per18 = shotsLost?.per_18 || {};
  return SHOT_BUCKETS.map(b => ({ ...b, shots: per18[b.key] }));
};

/**
 * The same buckets as RAW TOTALS over the holes actually logged.
 *
 * What a short sample can honestly say. The floor on this block exists because
 * a per-ROUND figure taken from nine holes is a projection — nine holes with
 * two penalties in them becomes "+4 penalty shots a round", which is a claim
 * about golf nobody played. The totals are not a projection: they are what
 * happened on those holes, and withholding them taught the player nothing
 * while their own data sat there.
 */
export const shotBucketTotals = (shotsLost) => {
  const totals = shotsLost?.totals || {};
  return SHOT_BUCKETS.map(b => ({ ...b, shots: totals[b.key] }));
};

const LEAK_WORDS = {
  tee_near: ['a tee shot in the first cut', 'finding the fairway'],
  // Deep is about the LIE, wide is about the DISTANCE off line — the wording
  // has to keep them apart here too, or the leak list quietly re-merges two
  // findings a player would act on differently. See TEE_MISS_META.
  tee_deep: ['a tee shot past the first cut', 'finding the fairway'],
  tee_trouble: ['a tee shot a long way off line', 'finding the fairway'],
  tee_penalty: ['a ball lost off the tee', 'finding the fairway'],
  miss_recoverable: ['a miss on the short grass', 'hitting the green'],
  miss_penal: ['a miss in sand or deep stuff', 'hitting the green'],
};

/**
 * What one situation costs, as a sentence with the figure held apart.
 *
 * The wording is deliberately "costs you" and never "would save you": the gap
 * is a difference between two of this player's own averages, and the holes
 * where a tee shot finds the trees are also the harder holes. The server says
 * the same thing in `definitions.cost_gap` — this is the reader's version.
 */
export const leakSentence = (leak) => {
  const words = LEAK_WORDS[leak?.key];
  if (!words) return null;
  const [what, instead] = words;
  return {
    text: `${what.charAt(0).toUpperCase()}${what.slice(1)} costs you %s more than ${instead}`,
    figure: `${leak.gap.toFixed(1)} shots`,
    per18: leak.per_18,
    holes: leak.holes,
  };
};

/**
 * The leaks worth showing, biggest first.
 *
 * Capped at three. The server ranks every situation that clears its floor, but
 * a list of six is a spreadsheet — a player can carry two or three things to
 * the first tee and no more.
 */
export const topLeaks = (shotsLost, limit = 3) =>
  (shotsLost?.leaks || []).slice(0, limit).map(leakSentence).filter(Boolean);

/**
 * How the putting bucket reads against the benchmark, or null.
 *
 * The ONE bucket that can be compared, because `putts_per_18` is already
 * denominated in strokes — see `shots_lost` on the server for why the other
 * two are left alone rather than given a number.
 */
export const puttingVerdict = (shotsLost) => {
  const delta = shotsLost?.benchmark?.putting_delta;
  if (delta == null) return null;
  if (Math.abs(delta) < 0.5) return 'about what a player off your handicap holes out in';
  return delta > 0
    ? `${delta.toFixed(1)} shots a round worse than a player off your handicap`
    : `${Math.abs(delta).toFixed(1)} shots a round better than a player off your handicap`;
};

/**
 * Whether the severity split has earned the right to be drawn at all.
 *
 * The row is optional on the entry screen, so a player can easily have one
 * answer in twenty misses — and a NEAR / DEEP / WIDE breakdown taken from one
 * shot would paint the whole hole. Exported because the corridor map and the
 * key that explains it must appear and disappear together: a key under a map
 * with nothing on it explains marks that are not there.
 */
export const severityGraded = (driving) =>
  !!driving?.misses && (driving.misses_graded || 0) / driving.misses >= 0.5;

/**
 * The benchmark comparison in EACH DEPARTMENT'S OWN UNIT.
 *
 * The page used to lead on `bands_behind_total` — a single figure, 12.1 in one
 * real case — and it could not be read. Two things were wrong with it:
 *
 *   · a "band" is a unit invented so four departments measured in four
 *     different things can be sorted on one axis. It does that job well and is
 *     meaningless as a quantity: the caption said a band is about five shots of
 *     handicap, which invites 12.1 x 5 = 60 shots, a nonsense;
 *   · it is a SUM of four independent comparisons. Being 7.8 bands behind on
 *     putting and 4.3 on the short game does not make you 12.1 of anything.
 *
 * So the comparison is shown the way a player already thinks about it: your
 * figure, the typical figure, and the gap between them in the unit the
 * department is measured in. Nothing is derived here that the server did not
 * already publish — this only pairs each figure with its benchmark.
 */
const BENCHMARK_FIELDS = {
  driving: { mine: (d) => d?.driving?.fairway_pct, mark: 'fairway_pct', unit: 'pct', better: 'higher' },
  approach: { mine: (d) => d?.approach?.gir_pct, mark: 'gir_pct', unit: 'pct', better: 'higher' },
  short_game: { mine: (d) => d?.short_game?.gross_scramble_pct, mark: 'gross_scramble_pct', unit: 'pct', better: 'higher' },
  putting: { mine: (d) => d?.putting?.putts_per_18, mark: 'putts_per_18', unit: 'putts', better: 'lower' },
};

/**
 * The gap, said in words.
 *
 * NEVER "pts". The gap between 57% of fairways and a typical 54% is three
 * PERCENTAGE POINTS, and "3 pts" in a golf app reads as three Stableford
 * points — a different quantity entirely, and one this screen also talks
 * about. The unit is already established by the two figures sitting directly
 * above the gap, so the gap only has to say how far and which way.
 */
export const benchmarkGap = (row) => {
  if (row.mine == null || row.mark == null) return null;
  const diff = row.better === 'lower' ? row.mine - row.mark : row.mark - row.mine;
  // Under a tenth of a putt, or half a point, is not a difference anybody
  // could act on.
  const floor = row.unit === 'putts' ? 0.1 : 0.5;
  if (Math.abs(diff) < floor) return { behind: false, level: true, diff, text: 'level' };
  const size = row.unit === 'putts' ? Math.abs(diff).toFixed(1) : Math.round(Math.abs(diff));
  const behind = diff > 0;
  if (row.unit === 'putts') {
    return { behind, level: false, diff, text: `${size} ${behind ? 'more' : 'fewer'} putts` };
  }
  // Better / worse, the verdict's words. "Ahead" and "behind" read as places
  // in a race, and the card above this table stopped using them.
  return { behind, level: false, diff, text: `${size} ${behind ? 'worse' : 'better'}` };
};

export const benchmarkRows = (data) => {
  const benchmark = data?.benchmark;
  if (!benchmark?.figures) return [];
  return AREAS.map((a) => {
    const f = BENCHMARK_FIELDS[a.key];
    const row = {
      key: a.key,
      label: a.label,
      tone: a.tone,
      unit: f.unit,
      better: f.better,
      mine: f.mine(data),
      mark: benchmark.figures[f.mark],
      // A department below its sample floor shows its figures and makes no
      // claim — the same rule the rest of this screen follows.
      enough: !!data?.sample?.enough?.[a.key],
    };
    return { ...row, gap: row.enough ? benchmarkGap(row) : null };
  });
};

/** "driving, approach and putting" — a list a person would read aloud. */
export const nameList = (names) => (names.length < 2
  ? (names[0] || '')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

const isAre = (names) => (names.length === 1 ? 'is' : 'are');

/**
 * THE STANDING, IN ONE SENTENCE — and it names BOTH SIDES.
 *
 * It used to name only the shortfalls ("Your short game and putting are behind
 * a typical 10–14 handicap"), which is true and is not an assessment: a player
 * eight points of fairways and nine of greens AHEAD of their band reads a
 * sentence about the two things they are behind in and concludes the whole
 * game is a problem. The strengths are also what make the weaknesses worth
 * acting on — they are where the score is coming from.
 *
 * Built from the same rows the comparison beneath it prints, in the order a
 * hole is played, so the sentence and the table can never disagree.
 */
export const standingSentence = (rows, band) => {
  const comparable = (rows || []).filter(r => r.gap);
  if (!comparable.length) return null;
  const worse = comparable.filter(r => r.gap.behind).map(r => r.label.toLowerCase());
  const better = comparable.filter(r => !r.gap.behind && !r.gap.level)
    .map(r => r.label.toLowerCase());
  // THE FRAME FIRST, ONCE. Every comparison on this card is against the same
  // player, and saying so at the head of the sentence is what lets the rest of
  // it use "better" and "worse" — the two words nobody can read the wrong way
  // round. "Ahead of" and "behind" read as positions in a race.
  const frame = `Compared with a typical ${band} handicapper`;
  if (!worse.length) {
    return `${frame}, nothing in your game is worse — everywhere there is enough logged to compare.`;
  }
  if (!better.length) {
    return `${frame}, your ${nameList(worse)} ${isAre(worse)} worse.`;
  }
  return `${frame}, your ${nameList(better)} ${isAre(better)} better `
    + `and your ${nameList(worse)} ${isAre(worse)} worse.`;
};

/** One row's gap, for a caller that needs to know how one department stands. */
export const departmentGap = (data, key) =>
  benchmarkRows(data).find(r => r.key === key)?.gap || null;

/** Putting's gap. Named because `decisionFor` is the one place that asks. */
export const puttingGap = (data) => departmentGap(data, 'putting');

/**
 * THE ONE DEPARTMENT TO WORK ON, ranked from the figures on screen.
 *
 * This used to rank the server's `bands_behind` while the rows beside it
 * ranked the published figures — two orderings of one list, which is how a
 * screen ends up arguing with itself. They genuinely differ: `benchmarkGap`
 * holds a dead band (half a point, a tenth of a putt) that `bands_behind` does
 * not, so a department the table calls LEVEL could be picked as the thing to
 * fix. Ranking the rows themselves is what makes the verdict and its evidence
 * one answer.
 *
 * Comparing across units still needs the band ruler — points of fairway and
 * putts per 18 do not compare — so the gap is divided by the server's own
 * published `spread_per_band`. The RULER is the server's; the FIGURES are the
 * ones the player is reading.
 *
 * Null unless two departments can be compared at all ("weakest of one" is not
 * a finding) and the worst is behind by half a band or more (naming a leader
 * in a level game invents a problem).
 */
export const weakestStanding = (data, { exclude = [] } = {}) => {
  const comparable = benchmarkRows(data).filter(r => r.gap);
  if (comparable.length < 2) return null;
  const spread = data?.benchmark?.spread_per_band || {};
  // `exclude` drops a CANDIDATE, never a comparison: a department left out
  // because its shortfall is another one's symptom still counts towards the
  // "two departments compared" rule — it was compared.
  const behind = comparable
    .filter(r => r.gap.behind && spread[r.key] > 0 && !exclude.includes(r.key))
    .map(r => ({ ...r, bands: r.gap.diff / spread[r.key] }))
    .sort((a, b) => b.bands - a.bands);
  return behind.length && behind[0].bands >= 0.5 ? behind[0] : null;
};
